import { useCallback } from 'react'
import { formatAmount } from '@/shared/lib'
import { en } from './dictionaries/en'
import {
  ru,
  type Dictionary,
  type PluralCategory,
  type PluralFormKey,
  type PluralKey,
  type TranslationKey,
} from './dictionaries/ru'
import { uz } from './dictionaries/uz'
import { getLanguage, useLanguageStore } from './language-store'
import { LANGUAGE_LOCALE, type Language } from './languages'

/**
 * Формы числа каждого языка — категории `Intl.PluralRules` (CLDR): русский
 * различает `one` / `few` / `many` (`other` — дроби), узбекский и английский —
 * `one` / `other`. Тип требует от словаря ровно те формы, которые язык выбирает.
 */
const DICTIONARIES: { ru: Dictionary<'one' | 'few' | 'many'>; uz: Dictionary; en: Dictionary } = { ru, uz, en }

export type TranslationParams = Record<string, string | number>

export type TranslateFn = (key: TranslationKey, params?: TranslationParams) => string

/**
 * Перевод с формой числа: `tn('locations.points', 4)` → «4 точки». `{count}` в
 * строке — число с неразрывными пробелами в разрядах, как у сумм; остальные
 * подстановки — в `params`.
 */
export type TranslatePluralFn = (key: PluralKey, count: number, params?: TranslationParams) => string

const interpolate = (template: string, params?: TranslationParams): string =>
  params ? template.replace(/\{(\w+)\}/g, (match, name: string) => String(params[name] ?? match)) : template

export const translate = (language: Language, key: TranslationKey, params?: TranslationParams): string =>
  interpolate(DICTIONARIES[language][key], params)

const pluralRules = new Map<Language, Intl.PluralRules | null>()

/** Категория числа для языка; без `Intl.PluralRules` (очень старый WebView) — `other`. */
const pluralCategory = (language: Language, count: number): PluralCategory => {
  if (!pluralRules.has(language)) {
    pluralRules.set(language, typeof Intl.PluralRules === 'function' ? new Intl.PluralRules(LANGUAGE_LOCALE[language]) : null)
  }

  return pluralRules.get(language)?.select(count) ?? 'other'
}

export const translatePlural = (
  language: Language,
  key: PluralKey,
  count: number,
  params?: TranslationParams,
): string => {
  const forms: Partial<Record<PluralFormKey, string>> = DICTIONARIES[language]
  const template = forms[`${key}.${pluralCategory(language, count)}`] ?? forms[`${key}.other`] ?? ''

  return interpolate(template, { count: formatAmount(count), ...params })
}

/** Перевод вне React: тексты уведомлений, заголовки MainButton в обработчиках. */
export const t: TranslateFn = (key, params) => translate(getLanguage(), key, params)

/** Перевод с формой числа вне React. */
export const tn: TranslatePluralFn = (key, count, params) => translatePlural(getLanguage(), key, count, params)

/** Перевод в компоненте: перерисовывает его при смене языка. `tn` — строки с формой числа. */
export const useTranslation = (): { t: TranslateFn; tn: TranslatePluralFn; language: Language } => {
  const language = useLanguageStore((state) => state.language)
  const tr = useCallback<TranslateFn>((key, params) => translate(language, key, params), [language])
  const trn = useCallback<TranslatePluralFn>(
    (key, count, params) => translatePlural(language, key, count, params),
    [language],
  )

  return { t: tr, tn: trn, language }
}
